import { formatLarge } from '@benzinga/utils';
import { ColumnDef } from '@benzinga/table';

import { WatchlistFinancials } from '../../../data';
import { hasWatchlistTickerAlerts } from '../../watchlistAlertStatus';
import {
  formatCurrency,
  LinkPortfolioCell,
  RESEARCH_SYMBOL_COLUMN_WIDTH,
  renderColoredValue,
  renderRange,
  SymbolCell,
  ResearchCell,
} from '../utils';
import { Holding } from '@benzinga/watchlist-manager';
import { RankingsColumnsDef } from './rankingsColsDef';
import { CryptoMetricsColumnsDef } from './cryptoColsDef';

type HoldingKeys = 'quantity' | 'costBasis';

const getHoldingsValue = (holdings?: Holding[] | Holding | null, key: HoldingKeys = 'quantity') => {
  if (!holdings) return 0;

  const value = Array.isArray(holdings)
    ? holdings?.reduce((acc, holding) => acc + (holding?.[key] ?? 0), 0)
    : (holdings?.[key] ?? 0);
  return value;
};

export const OverviewColumnsDef: ColumnDef<WatchlistFinancials>[] = [
  {
    field: 'symbol',
    headerName: 'Symbol',
    width: RESEARCH_SYMBOL_COLUMN_WIDTH,
    sortEnabled: true,
    cellRenderer: ({ data }) => <SymbolCell record={data} />,
    valueGetter: ({ data }) => data.symbol || '',
  },
  {
    field: 'quote_last',
    headerName: 'Price',
    sortEnabled: true,
    valueGetter: ({ data }) => data.quote?.lastTradePrice ?? 0,
    cellRenderer: ({ value }) => <span>{formatCurrency(value)}</span>,
  },
  {
    field: 'quote_change',
    headerName: 'Change',
    sortEnabled: true,
    valueGetter: ({ data }) => data.quote?.change ?? 0,
    cellRenderer: ({ value }) => renderColoredValue(value, 'currency'),
  },
  {
    field: 'quote_change_perc',
    headerName: 'Change %',
    sortEnabled: true,
    valueGetter: ({ data }) => data.quote?.changePercent ?? 0,
    cellRenderer: ({ value }) => renderColoredValue(value, 'percentage'),
  },
  {
    field: 'quote_volume',
    headerName: 'Volume',
    sortEnabled: true,
    valueGetter: ({ data }) => data.quote?.volume ?? 0,
    cellRenderer: ({ value }) => (value ? formatLarge(value) : '-'),
  },
  {
    field: 'quote_market_cap',
    headerName: 'Market Cap',
    sortEnabled: true,
    valueGetter: ({ data }) => data.quote?.marketCap ?? 0,
    cellRenderer: ({ value }) => (value ? formatLarge(value) : '-'),
  },
  {
    field: 'value__p_e_ttm',
    headerName: 'PE (TTM)',
    sortEnabled: true,
    valueGetter: ({ data }) => data.financials?.valuationRatios?.[0]?.peRatio ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-10">
        {value.toFixed(2)}
      </ResearchCell>
    ),
  },
  {
    field: 'quote_day_range',
    headerName: 'Day Range',
    sortEnabled: true,
    width: 160,
    valueGetter: ({ data }) => data.quote?.low ?? 0,
    cellRenderer: ({ data }) => renderRange(data.quote?.low, data.quote?.high, data.quote?.lastTradePrice),
  },
  {
    field: 'quote_52w_range',
    headerName: '52W Range',
    sortEnabled: true,
    width: 160,
    valueGetter: ({ data }) => data.quote?.fiftyTwoWeekLow ?? 0,
    cellRenderer: ({ data }) =>
      renderRange(data.quote?.fiftyTwoWeekLow, data.quote?.fiftyTwoWeekHigh, data.quote?.lastTradePrice),
  },
  {
    field: 'quote_shares_outstanding',
    headerName: 'Shares Out',
    sortEnabled: true,
    valueGetter: ({ data }) => data.quote?.sharesOutstanding ?? 0,
    cellRenderer: ({ value }) => (value ? formatLarge(value) : '-'),
  },
  {
    field: 'quote_float',
    headerName: 'Float',
    sortEnabled: true,
    valueGetter: ({ data }) => data.quote?.sharesFloat ?? 0,
    cellRenderer: ({ value }) => (value ? formatLarge(value) : '-'),
  },
];

export const HoldingsOverviewColumnsDef: ColumnDef<WatchlistFinancials>[] = [
  ...OverviewColumnsDef.slice(0, 5),
  {
    field: 'holding_quantity',
    headerName: 'Qty',
    sortEnabled: true,
    valueGetter: ({ data }) => getHoldingsValue(data.holdings, 'quantity'),
    cellRenderer: ({ value }) => {
      const quantity = value;
      return quantity > 0 ? <span>{quantity.toFixed(2)}</span> : <LinkPortfolioCell />;
    },
  },
  {
    field: 'holding_current_value',
    headerName: 'Market Value',
    sortEnabled: true,
    valueGetter: ({ data }) => {
      const quantity = getHoldingsValue(data.holdings, 'quantity');
      return quantity * (data.quote?.lastTradePrice ?? 0);
    },
    cellRenderer: ({ data, value }) => {
      const quantity = getHoldingsValue(data.holdings, 'quantity');
      return <span>{quantity > 0 && data.quote?.lastTradePrice ? formatCurrency(value) : '-'}</span>;
    },
  },
  {
    field: 'holding_original_value',
    headerName: 'Cost',
    sortEnabled: true,
    valueGetter: ({ data }) => getHoldingsValue(data.holdings, 'costBasis'),
    cellRenderer: ({ value }) => <span>{value ? formatCurrency(value) : '-'}</span>,
  },
  {
    field: 'holding_gain_dollar',
    headerName: 'Gain($)',
    sortEnabled: true,
    valueGetter: ({ data }) => {
      const quantity = getHoldingsValue(data.holdings, 'quantity');
      const costBasis = getHoldingsValue(data.holdings, 'costBasis');
      return quantity * (data.quote?.lastTradePrice ?? 0) - costBasis;
    },
    cellRenderer: ({ data, value }) => {
      const quantity = getHoldingsValue(data.holdings, 'quantity');
      return quantity > 0 && data.quote?.lastTradePrice ? renderColoredValue(value, 'currency') : '-';
    },
  },
  {
    field: 'holding_gain_perc',
    headerName: 'Gain(%)',
    sortEnabled: true,
    valueGetter: ({ data }) => {
      const quantity = getHoldingsValue(data.holdings, 'quantity');
      const costBasis = getHoldingsValue(data.holdings, 'costBasis');
      const gain = quantity * (data.quote?.lastTradePrice ?? 0) - costBasis;
      return costBasis > 0 ? (gain / costBasis) * 100 : 0;
    },
    cellRenderer: ({ data, value }) => {
      const quantity = getHoldingsValue(data.holdings, 'quantity');
      return quantity > 0 && data.quote?.lastTradePrice ? renderColoredValue(value, 'percentage') : '-';
    },
  },
];

export const WatchlistColumnsDef: ColumnDef<WatchlistFinancials>[] = [
  ...OverviewColumnsDef.slice(0, 6),
  {
    field: 'alerts',
    headerName: 'Alerts',
    valueGetter: ({ data }) => Number(hasWatchlistTickerAlerts(data.watchlistSymbol)),
    cellRenderer: ({ value }) => (
      <span className={value ? 'font-semibold text-bzblue-700' : 'text-slate-400'}>{value ? 'On' : 'Off'}</span>
    ),
  },
];

const cryptoOverviewFields = new Set([
  'symbol',
  'quote_last',
  'holding_quantity',
  'holding_current_value',
  'holding_original_value',
  'holding_gain_dollar',
  'holding_gain_perc',
]);

export const CryptoOverviewColumnsDef: ColumnDef<WatchlistFinancials>[] = [
  ...HoldingsOverviewColumnsDef.filter(col => cryptoOverviewFields.has(col.field as string)),
  ...CryptoMetricsColumnsDef,
  ...RankingsColumnsDef.filter(col => col.field === 'momentum_ranking'),
];
